import React, { forwardRef, useRef } from "react";
import styled from "styled-components";

import { WorkspacePlanTier } from "common/interfaces";
import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import CheckoutController, { CheckoutControllerInitializer, CheckoutControllerState, OnSuccessParams } from "js/controllers/CheckoutController";
import { BeautifulDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { useScreenSize } from "js/react/hooks/useScreenSize";
import { AddressForm } from "js/react/views/Billing/Checkout/components/AddressForm/AddressForm";
import { BillingIntervalText } from "js/react/views/Billing/Checkout/components/BillingIntervalText";
import { PaymentForm } from "js/react/views/Billing/Checkout/components/PaymentForm";
import { PlanFeatures } from "js/react/views/Billing/Checkout/components/PlanFeatures";
import { PlanSummary } from "js/react/views/Billing/Checkout/components/PlanSummary";
import { PromoForm } from "js/react/views/Billing/Checkout/components/PromoForm";
import { SubmissionButtons } from "js/react/views/Billing/Checkout/components/SubmissionButtons";
import { TermsOfUseAndRefundPolicy } from "js/react/views/Billing/Checkout/components/TermsOfUseAndRefundPolicy";

const CheckoutContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-self: stretch;
    min-height: 0;
    overflow-y: auto;
    padding: 15px 30px;
`;

const PaymentAndAddressContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: 5px;
    padding-left: 5px;
    gap: 15px;
    min-height: 0;
    margin-top: 10px;
`;

const CheckoutButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-shrink: 0;
    background: #fff;
    padding-top: 20px;
    z-index: 1;
`;

const MobileViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    background: #FFF;
    overflow: hidden;
    position: relative;
`;

const StickyHeader = styled.div`
    display: flex;
    padding: 30px 30px 10px;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 5px;
    flex-shrink: 0;
    background: #FFF;
`;

const ScrollContent = styled.div`
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    padding: 0 30px 20px 30px;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 20px;
    flex-grow: 1;
`;

const StickyBottom = styled.div`
    display: flex;
    padding: 20px 30px;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;
    background: #F7FAFC;
    box-shadow: 0 -2px 25px 0 rgba(0, 0, 0, 0.08);
    z-index: 10;
`;

const OverlayContainer = styled.div<{ useMobileLayout?: boolean, useFullScreen?: boolean, fillHeight?: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: #FFF;
    z-index: 1000;
    overflow: hidden;
    max-height: 100vh;

    height: ${({ fillHeight }) => fillHeight ? "76vh" : "unset"};
    width: ${({ useMobileLayout }) => useMobileLayout ? "600px" : "825px"};

    border-radius: 4px;
    box-shadow: 0px 9px 37px 0px rgba(0, 0, 0, 0.2);

    ${({ useMobileLayout }) => useMobileLayout && `
        height: 90vh;
        width: 600px;
    `}
    ${({ useFullScreen }) => useFullScreen && `
        height: 100vh;
        height: 100dvh;
        width: 100vw;
    `}
`;

const CheckoutViewContainer = styled.div`
    align-self: stretch;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    padding-right: 0;

    .dialog-content {
        padding: 0 34px;
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;
    }
`;

const PlanFeaturesContainerMobile = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;

const MobileView = CheckoutController.withState(
    function MobileView({ fetching }: CheckoutControllerState) {
        const submitRef = useRef<any>(null);

        const { title, subtitle, disclaimer } = CheckoutController.labels;

        return (
            <MobileViewContainer>
                <FetchingClickShield visible={fetching} backgroundColor="white" />

                <StickyHeader>
                    <TextContent dialogTitle center>{title}</TextContent>
                    {subtitle && <TextContent dialogSubtitle center>
                        {subtitle}
                    </TextContent>}
                </StickyHeader>

                <ScrollContent>
                    <PaymentForm />
                    <AddressForm nextFieldRef={submitRef} />
                    <TextContent disclaimer>
                        {disclaimer}
                    </TextContent>
                    <PromoForm />
                    <PlanFeaturesContainerMobile>
                        <PlanFeatures useNarrowFormat={true} />
                    </PlanFeaturesContainerMobile>
                </ScrollContent>

                <StickyBottom>
                    <PlanSummary expandable />

                    <SubmissionButtons ref={submitRef} />

                    <TextContent disclaimer center style={{ marginTop: -10, marginBottom: -10 }}>
                        <BillingIntervalText
                            includeTerms
                            termsComponent={<TermsOfUseAndRefundPolicy textDecoration="none" />}
                        />
                    </TextContent>
                </StickyBottom>
            </MobileViewContainer>
        );
    }
);

const DesktopView = CheckoutController.withState(
    function DesktopView() {
        const submitRef = useRef<any>(null);

        const { title, subtitle, disclaimer } = CheckoutController.labels;

        return (
            <FlexLayout justify="center" align="stretch" width="fill" height="fill" padding="20px 20px 0" gap={20}>
                <CheckoutContainer>
                    <FlexLayout direction="column" gap={5}>
                        <TextContent dialogTitle>{title}</TextContent>
                        {subtitle && <TextContent dialogSubtitle>
                            {subtitle}
                        </TextContent>}
                    </FlexLayout>

                    <PaymentAndAddressContainer>
                        <PaymentForm />
                        <AddressForm nextFieldRef={submitRef} />
                        <TextContent disclaimer>
                            {disclaimer}
                        </TextContent>
                    </PaymentAndAddressContainer>

                    <CheckoutButtonsContainer>
                        <SubmissionButtons ref={submitRef} />

                        <TextContent disclaimer center>
                            <TermsOfUseAndRefundPolicy size="small" />
                        </TextContent>
                        <TextContent disclaimer center>
                            <BillingIntervalText />
                        </TextContent>
                    </CheckoutButtonsContainer>
                </CheckoutContainer>

                <FlexLayout direction="column" gap={20} height="fill" scrollable="vertical" minWidth={280} padding="0 0 20px 0">
                    <PlanSummary />
                    <PlanFeatures />
                </FlexLayout>
            </FlexLayout>
        );
    }
);

export const CheckoutView = CheckoutController.withStripeElementsAndSession(
    function CheckoutView({ fillHeight = true }: { fillHeight?: boolean }) {
        const { isMobile, width: windowWidth } = useScreenSize();

        const useMobileLayout = isMobile || windowWidth < 916;
        const useFullScreen = isMobile || windowWidth < 652;
        if (useMobileLayout || useFullScreen) {
            fillHeight = true;
        }

        return (
            <OverlayContainer useMobileLayout={useMobileLayout} useFullScreen={useFullScreen} fillHeight={fillHeight}>
                <CheckoutViewContainer>
                    {useMobileLayout && <MobileView />}
                    {!useMobileLayout && <DesktopView />}
                </CheckoutViewContainer>
            </OverlayContainer>
        );
    },
    { appearanceVariant: "B" }
);

const StyledDialog = styled(BeautifulDialog)`
    &&& {
        div.MuiPaper-root {
            >div.spinner {
                position: relative !important;
                min-height: min(600px, 80vh);
                min-width: min(600px, 80vw);
            }
        }
    }
`;

export const CheckoutDialog = forwardRef(
    function CheckoutDialog(
        {
            closeDialog,
            workspaceId,
            tier,
            billingInterval
        }: {
            closeDialog: (success?: boolean, params?: OnSuccessParams) => void,
            workspaceId: string,
            tier: WorkspacePlanTier.PRO | WorkspacePlanTier.TEAM,
            billingInterval?: "month" | "year"
        },
        ref: React.Ref<any>
    ) {
        return (
            <StyledDialog
                closeDialog={closeDialog}
                PaperProps={{ style: { maxHeight: "100%", backgroundColor: "white", margin: 0, width: "auto", maxWidth: "none" } }}
                ref={ref}
            >
                <CheckoutControllerInitializer
                    tier={tier}
                    workspaceId={workspaceId}
                    billingInterval={billingInterval}
                    onSuccess={params => closeDialog(true, params)}
                    onCancel={() => closeDialog(false)}
                >
                    <CheckoutView fillHeight={false} />
                </CheckoutControllerInitializer>
            </StyledDialog>
        );
    }
);
